/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import VerticalSlider from '../../components/chat/VerticalSlider';
import ConversationHistory from '../../components/chat/ConversationHistory';
import ChatPanel from '../../components/chat/ChatPanel';
import UserService, { UserProfile } from '../../services/user.service';
import { useAuth } from '../../context/AuthContext';
//import { Message } from '../../services/openai.service';

const ChatPage: React.FC = () => {
  const { userInfo } = useAuth();
  const [profileImage, setProfileImage] = useState<string | undefined>(undefined);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  //const [error, setError] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const [showWelcome, setShowWelcome] = useState<boolean>(true);

  // Estados para el diseño responsive
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showConversationHistory, setShowConversationHistory] = useState<boolean>(false);

  // Función para obtener el perfil del usuario desde el estado global o el backend si es necesario
  const fetchUserProfile = async () => {
    // Verificar si tenemos la información del usuario en el estado global
    if (userInfo && userInfo.token) {
      //console.log('ChatPage: Usando información del usuario desde el estado global');

      // Crear un perfil de usuario a partir de la información global
      const profile: UserProfile = {
        id: '',
        business: userInfo.business || 0,
        email: userInfo.email,
        name: userInfo.name,
        lastName: userInfo.lastname,
        profilePictureUrl: userInfo.profileImage || '',
        profileImage: userInfo.profileImage || '',
        isActive: true,
        state: 'active',
        leadPrice: userInfo.leadPrice || 0,
        role: localStorage.getItem('user_role') || 'user'
      };

      setUserProfile(profile);
      setIsAuthenticated(true);

      // Establecer la imagen de perfil
      if (userInfo.profileImage) {
        // console.log('ChatPage: Usando imagen de perfil desde estado global:', userInfo.profileImage);
        setProfileImage(userInfo.profileImage);
      }

      return profile;
    }

    // Si no tenemos la información en el estado global, verificar si hay un token de autenticación
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) {
      setIsAuthenticated(false);
      return;
    }

    try {
      console.log('ChatPage: Obteniendo perfil del usuario desde el backend (fallback)...');
      const profile = await UserService.getCurrentUserProfile();
      setUserProfile(profile);
      setIsAuthenticated(true);

      // Verificar si el usuario tiene una imagen de perfil
      if (profile.profilePictureUrl) {
        console.log('ChatPage: Imagen de perfil obtenida:', profile.profilePictureUrl);
        setProfileImage(profile.profilePictureUrl);
      } else if (profile.profileImage) {
        console.log('ChatPage: Imagen de perfil alternativa obtenida:', profile.profileImage);
        setProfileImage(profile.profileImage);
      } else {
        console.log('ChatPage: El usuario no tiene imagen de perfil');
      }

      return profile;
    } catch (error) {
      console.error('ChatPage: Error al obtener el perfil del usuario:', error);
      //setError('Error al obtener el perfil del usuario');
      return null;
    }
  };

  // Verificar si el usuario está autenticado al cargar el componente
  useEffect(() => {
    const checkAuth = async () => {
      // Si tenemos información del usuario en el estado global, usarla directamente
      if (userInfo && userInfo.token) {
        await fetchUserProfile();
      } else {
        // Verificar si hay un token en localStorage como fallback
        const token = localStorage.getItem('auth_token');
        if (token) {
          await fetchUserProfile();
        } else {
          setIsAuthenticated(false);
          console.log('ChatPage: Usuario no autenticado');
        }
      }
    };

    checkAuth();

    // Escuchar eventos de autenticación
    const handleAuthEvent = () => {
      checkAuth();
    };

    window.addEventListener('auth-success', handleAuthEvent);
    window.addEventListener('auth-logout', handleAuthEvent);

    return () => {
      window.removeEventListener('auth-success', handleAuthEvent);
      window.removeEventListener('auth-logout', handleAuthEvent);
    };
  }, []);

  // Función para manejar la selección de una conversación
  const handleSelectConversation = (conversation: any) => {
    if (conversation) {
      setSelectedConversation(conversation);
      setShowWelcome(false);
    }
  };

  // Función para ocultar la pantalla de bienvenida cuando se envía un mensaje
  const handleHideWelcome = () => {
    setShowWelcome(false);
  };

  // Función para manejar la creación de una nueva conversación
  const handleNewConversation = () => {
    // Reiniciar el estado de la conversación
    setSelectedConversation(null);
    // Mostrar la pantalla de bienvenida
    setShowWelcome(true);
    // Cerrar el historial de conversaciones en móvil
    setShowConversationHistory(false);
    // Disparar un evento personalizado para que ChatPanel reinicie sus mensajes
    const resetEvent = new CustomEvent('reset-conversation');
    window.dispatchEvent(resetEvent);
    console.log('ChatPage: Iniciando nueva conversación');
  };

  // Función para alternar la visibilidad del historial de conversaciones
  const toggleConversationHistory = () => {
    setShowConversationHistory(!showConversationHistory);
  };

  // Función para cerrar el historial de conversaciones
  const closeConversationHistory = () => {
    setShowConversationHistory(false);
  };

  // Función para manejar la selección de conversación en móvil
  const handleSelectConversationMobile = (conversation: any) => {
    handleSelectConversation(conversation);
    // Cerrar el historial en móvil después de seleccionar
    if (isMobile) {
      setShowConversationHistory(false);
    }
  };

  // Efecto para detectar el tamaño de pantalla
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint en Tailwind
    };

    // Verificar tamaño inicial
    checkScreenSize();

    // Agregar listener para cambios de tamaño
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Efecto para resetear la animación de bienvenida cuando se actualiza la página
  useEffect(() => {
    // Al cargar la página, siempre mostrar la animación de bienvenida
    setShowWelcome(true);
  }, []);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-xervix-bg-primary text-xervix-text-primary dark:bg-xervix-bg-primary-dark dark:text-xervix-text-primary-dark">
      {/* Layout Desktop */}
      <div className={`h-[94vh] w-[98%] ${isMobile ? 'hidden' : 'flex'} overflow-hidden rounded-[24px]`}>
        <VerticalSlider profileImage={profileImage} />
        <ConversationHistory
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
        />
        <div className="flex-1 flex flex-col">
          <ChatPanel
            profileImage={profileImage}
            isAuthenticated={isAuthenticated}
            userProfile={userProfile}
            selectedConversation={selectedConversation}
            showWelcome={showWelcome}
            onHideWelcome={handleHideWelcome}
          />
        </div>
      </div>

      {/* Layout Mobile */}
      <div className={`h-screen w-full ${isMobile ? 'flex' : 'hidden'} flex-col`}>
        {/* Header horizontal */}
        <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark h-16 flex items-center justify-between px-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-xervix-primary rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">X</div>
            <span className="text-xervix-primary font-bold text-lg">XERVIX</span>
          </div>
          <VerticalSlider profileImage={profileImage} isMobile={true} />
        </div>

        {/* Barra con hamburguesa */}
        <div className="bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark h-12 flex items-center px-4">
          <button onClick={toggleConversationHistory} className="w-8 h-8 flex items-center justify-center text-xervix-text-primary dark:text-xervix-text-primary-dark hover:bg-xervix-bg-tertiary dark:hover:bg-xervix-bg-tertiary-dark rounded">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="ml-3 text-sm text-xervix-text-muted dark:text-xervix-text-muted-dark">Conversaciones</span>
        </div>

        {/* Chat Panel */}
        <div className="flex-1 overflow-hidden">
          <ChatPanel
            profileImage={profileImage}
            isAuthenticated={isAuthenticated}
            userProfile={userProfile}
            selectedConversation={selectedConversation}
            showWelcome={showWelcome}
            onHideWelcome={handleHideWelcome}
            isMobile={true}
          />
        </div>

        {/* Overlay Conversaciones */}
        {showConversationHistory && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={closeConversationHistory} />
            <div className="fixed top-0 left-0 h-full w-4/5 bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark z-50">
              <div className="h-full flex flex-col">
                <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark h-16 flex items-center justify-between px-4">
                  <span className="text-xervix-text-primary dark:text-xervix-text-primary-dark font-semibold">Conversaciones</span>
                  <button onClick={closeConversationHistory} className="w-8 h-8 flex items-center justify-center">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex-1 overflow-hidden">
                  <ConversationHistory
                    onSelectConversation={handleSelectConversationMobile}
                    onNewConversation={handleNewConversation}
                    isMobile={true}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
